import React from "react";
import {
  Box,
  Button,
  Container,
  Flex,
  Heading,
  Image,
  SimpleGrid,
  Stack,
  Text,
} from "@chakra-ui/react";
import ContactModal from "../../components/ContactModal";

const researchAreas = [
  {
    number: "01",
    title: "Preventive & Precision Health",
    text: "Research focused on identifying risk earlier and understanding how prevention can become more precise, contextual and actionable.",
  },
  {
    number: "02",
    title: "African Health Data & Longitudinal Evidence",
    text: "Building stronger evidence from African populations through better health data, longitudinal observation and contextually relevant research.",
  },
  {
    number: "03",
    title: "Biomarkers & Genomics",
    text: "Exploring how biomarkers, genomics and other biological signals can contribute to earlier detection, stratification and more precise approaches to care.",
  },
  {
    number: "04",
    title: "Translational Science",
    text: "Studying how scientific findings can move beyond discovery into usable technologies, clinical practice and measurable health outcomes.",
  },
  {
    number: "05",
    title: "Implementation Research",
    text: "Generating evidence about what works in real-world African healthcare settings and the conditions required for reliable adoption and delivery.",
  },
  {
    number: "06",
    title: "Research Capacity & Scientific Exchange",
    text: "Creating opportunities for researchers, clinicians and emerging scientists to develop, collaborate and exchange knowledge across disciplines and institutions.",
  },
];

const engagementAreas = [
  {
    title: "Research collaboration",
    text: "Work with the Institute on research questions, studies and evidence generation.",
  },
  {
    title: "Implementation projects",
    text: "Collaborate on projects testing how promising health innovations work in real-world settings.",
  },
  {
    title: "Seminars & expert discussions",
    text: "Participate in scientific conversations spanning research, clinical practice, technology and implementation.",
  },
  {
    title: "Calls for collaborators",
    text: "Respond to specific opportunities where your expertise can contribute to an active initiative.",
  },
  {
    title: "Pilot opportunities",
    text: "Explore opportunities to test promising technologies, research ideas or health pathways.",
  },
  {
    title: "Fellowships & funding",
    text: "Discover relevant opportunities for researchers and emerging scientists.",
  },
  {
    title: "Curated opportunities",
    text: "Selected opportunities relevant to preventive and precision health.",
  },
  {
    title: "Knowledge resources",
    text: "Access briefs, concept notes, seminar outputs and other resources emerging from the work.",
  },
  {
    title: "Cross-disciplinary introductions",
    text: "Connect researchers, clinicians, innovators and institutions working on complementary problems.",
  },
];

const seminars = [
  {
    number: "01",
    status: "Completed",
    date: "28 August 2026",
    title: "Building Africa's Precision Health Future",
    topic: "From Genomics and Data Science to AI and Clinical Practice",
    speakers: [
      {
        name: "Dr Ahmad Jibril Babah",
        role: "Senior Special Assistant, Sector-Wide Coordination, Office of the Special Adviser to the President on Health",
      },
      {
        name: "Dr Jihenne Ben Aissa-Haj",
        role: "Biomarker Program Manager, MCPHARMA",
      },
    ],
    output: "Implementation Brief",
    followOn: "90-Day Concept Note",
  },
  {
    number: "02",
    status: "Completed",
    date: "6 October 2026",
    title: "From African Health Data to Actionable Intelligence",
    topic: null,
    description:
      "The discussion examined how locally relevant evidence, structured health data and existing care pathways can connect research to better decisions and care.",
    speakers: [
      {
        name: "Prof. Solomon Rotimi",
        role: "Professor of Clinical Biochemistry and leading expert in cancer genomics",
      },
      {
        name: "Dr Olakunle Oladehin",
        role: "Visionary medical doctor, global health expert, and digital health architect",
      },
    ],
    summaryUrl:
      "/institute/seminars/from-african-health-data-to-actionable-intelligence",
  },
  {
    number: "03",
    status: "Proposed",
    timing: "Tentatively targeted for the first week of November 2026",
    title: "From Innovation to Implementation",
    topic:
      "How do health technologies become functioning health-system capabilities?",
  },
  {
    number: "04",
    status: "Proposed",
    date: null,
    title: "Building the Infrastructure for African Precision Health",
    topic: null,
  },
  {
    number: "05",
    status: "Proposed",
    date: null,
    title:
      "Who Owns African Health Data? Governance, Trust and Value in the Age of Genomics and AI?",
    topic: null,
  },
];

function InstitutePage() {
  return (
    <Box className="serena-page">
      {/* HERO */}
      <Box bg="#f3f0e8">
        <Container
          maxW="1400px"
          px={{ base: 5, md: 8, lg: 12 }}
          py={{ base: 20, md: 28, lg: 32 }}
        >
          <SimpleGrid
            columns={{ base: 1, lg: 2 }}
            spacing={{ base: 12, lg: 20 }}
            alignItems="center"
          >
            <Stack spacing={7}>
              <Text
                fontSize="xs"
                fontWeight="700"
                letterSpacing="0.16em"
                textTransform="uppercase"
                color="#087f73"
              >
                Serena Space Institute
              </Text>

              <Heading
                as="h1"
                fontFamily="'Poppins', sans-serif"
                fontSize={{ base: "3xl", md: "5xl", lg: "6xl" }}
                lineHeight="1.05"
                letterSpacing="-0.045em"
                fontWeight="600"
                color="#17211f"
                maxW="720px"
              >
                Building the evidence behind better health decisions.
              </Heading>

              <Text
                fontSize={{ base: "lg", md: "xl" }}
                lineHeight="1.75"
                color="#4c5955"
                maxW="650px"
              >
                The Serena Space Institute is the scientific and knowledge
                engine of Serena Space — focused on preventive and precision
                health, African health data, translational science and the
                research capacity needed to move evidence into practice.
              </Text>

              <Flex gap={4} flexWrap="wrap">
                <Button
                  as="a"
                  href="#research"
                  bg="#087f73"
                  color="white"
                  borderRadius="full"
                  px={6}
                  size="lg"
                  fontWeight="600"
                  _hover={{ bg: "#06695f" }}
                >
                  Explore our research
                </Button>

                <Button
                  as="a"
                  href="#seminars"
                  variant="outline"
                  borderColor="#17211f"
                  color="#17211f"
                  borderRadius="full"
                  px={6}
                  size="lg"
                  fontWeight="600"
                  _hover={{ bg: "blackAlpha.50" }}
                >
                  Explore the programme
                </Button>
              </Flex>
            </Stack>

            <Box borderRadius="2xl" overflow="hidden">
              <Image
                src="/images/institutional/serena6.png"
                alt="African scientists collaborating around scientific research"
                w="100%"
                h={{ base: "320px", md: "500px" }}
                objectFit="cover"
              />
            </Box>
          </SimpleGrid>
        </Container>
      </Box>

      {/* WHY THE INSTITUTE */}
      <Box bg="#f9faf8">
        <Container
          maxW="1200px"
          px={{ base: 5, md: 8 }}
          py={{ base: 20, md: 28 }}
        >
          <SimpleGrid
            columns={{ base: 1, md: 2 }}
            spacing={{ base: 10, md: 20 }}
          >
            <Box>
              <Text
                fontSize="xs"
                fontWeight="700"
                letterSpacing="0.14em"
                textTransform="uppercase"
                color="#087f73"
                mb={5}
              >
                Why the Institute exists
              </Text>

              <Heading
                fontFamily="'Poppins', sans-serif"
                fontSize={{ base: "2xl", md: "4xl" }}
                lineHeight="1.15"
                letterSpacing="-0.035em"
                fontWeight="600"
              >
                Africa's precision-health future needs more than technology.
              </Heading>
            </Box>

            <Stack spacing={6}>
              <Text
                fontSize={{ base: "md", md: "lg" }}
                lineHeight="1.8"
                color="#4c5955"
              >
                Africa needs stronger evidence about its populations,
                environments, disease patterns and healthcare realities — and
                stronger scientific capacity to turn that evidence into
                something useful.
              </Text>

              <Text
                fontSize={{ base: "md", md: "lg" }}
                lineHeight="1.8"
                color="#4c5955"
              >
                The Institute exists to help generate that evidence, develop
                the scientific capacity around it and understand how knowledge
                can move from discovery toward technologies, clinical practice
                and measurable outcomes.
              </Text>
            </Stack>
          </SimpleGrid>
        </Container>
      </Box>

      {/* RESEARCH AREAS */}
      <Box id="research" bg="#17211f" color="white">
        <Container
          maxW="1400px"
          px={{ base: 5, md: 8, lg: 12 }}
          py={{ base: 20, md: 28 }}
        >
          <Stack spacing={6} mb={{ base: 12, md: 16 }}>
            <Text
              fontSize="xs"
              fontWeight="700"
              letterSpacing="0.16em"
              textTransform="uppercase"
              color="#7bcfc3"
            >
              Research areas
            </Text>

            <Heading
              fontFamily="'Poppins', sans-serif"
              fontSize={{ base: "3xl", md: "5xl" }}
              lineHeight="1.1"
              letterSpacing="-0.04em"
              fontWeight="600"
              maxW="850px"
            >
              From biological signals to real-world health outcomes.
            </Heading>
          </Stack>

          <SimpleGrid
            columns={{ base: 1, md: 2, lg: 3 }}
            spacing="1px"
            bg="whiteAlpha.200"
          >
            {researchAreas.map((area) => (
              <Box
                key={area.number}
                bg="#17211f"
                p={{ base: 7, md: 9 }}
                minH="250px"
              >
                <Text
                  fontSize="xs"
                  fontWeight="700"
                  letterSpacing="0.12em"
                  color="whiteAlpha.500"
                  mb={8}
                >
                  {area.number}
                </Text>

                <Heading
                  as="h3"
                  fontFamily="'Poppins', sans-serif"
                  fontSize="xl"
                  lineHeight="1.25"
                  fontWeight="600"
                  mb={4}
                >
                  {area.title}
                </Heading>

                <Text
                  fontSize="sm"
                  lineHeight="1.75"
                  color="whiteAlpha.700"
                >
                  {area.text}
                </Text>
              </Box>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* INSTITUTE IN MOTION */}
      <Box id="seminars" bg="#f3f0e8">
        <Container
          maxW="1200px"
          px={{ base: 5, md: 8 }}
          py={{ base: 20, md: 28 }}
        >
          <Stack spacing={6} mb={{ base: 12, md: 16 }}>
            <Text
              fontSize="xs"
              fontWeight="700"
              letterSpacing="0.16em"
              textTransform="uppercase"
              color="#087f73"
            >
              Institute in motion
            </Text>

            <Heading
              fontFamily="'Poppins', sans-serif"
              fontSize={{ base: "3xl", md: "5xl" }}
              lineHeight="1.1"
              letterSpacing="-0.04em"
              fontWeight="600"
              maxW="850px"
            >
              A growing programme of scientific exchange.
            </Heading>

            <Text
              fontSize={{ base: "md", md: "lg" }}
              lineHeight="1.8"
              color="#4c5955"
              maxW="800px"
            >
              The Institute brings researchers, clinicians, policymakers,
              technologists and other experts together around questions at the
              intersection of scientific discovery and real-world healthcare.
            </Text>
          </Stack>

          <Stack spacing={0}>
            {seminars.map((seminar) => (
              <Box
                key={seminar.number}
                py={{ base: 8, md: 10 }}
                borderTop="1px solid"
                borderColor="blackAlpha.200"
              >
                <SimpleGrid
                  columns={{ base: 1, md: 12 }}
                  spacing={{ base: 5, md: 8 }}
                >
                  <Box gridColumn={{ md: "span 1" }}>
                    <Text
                      fontSize="sm"
                      fontWeight="700"
                      color="#087f73"
                    >
                      {seminar.number}
                    </Text>
                  </Box>

                  <Box gridColumn={{ md: "span 8" }}>
                    <Flex
                      gap={3}
                      flexWrap="wrap"
                      align="center"
                      mb={3}
                    >
                      <Text
                        fontSize="xs"
                        fontWeight="700"
                        letterSpacing="0.1em"
                        textTransform="uppercase"
                        color="#5c6965"
                      >
                        {seminar.status}
                      </Text>

                      {(seminar.date || seminar.timing) && (
                        <Text fontSize="sm" color="#6b7773">
                          · {seminar.date || seminar.timing}
                        </Text>
                      )}
                    </Flex>

                    <Heading
                      as="h3"
                      fontFamily="'Poppins', sans-serif"
                      fontSize={{ base: "2xl", md: "3xl" }}
                      lineHeight="1.2"
                      letterSpacing="-0.03em"
                      fontWeight="600"
                      mb={3}
                    >
                      {seminar.title}
                    </Heading>

                    {seminar.topic && (
                      <Text
                        fontSize={{ base: "md", md: "lg" }}
                        fontStyle="italic"
                        color="#4c5955"
                        mb={5}
                      >
                        {seminar.topic}
                      </Text>
                    )}

                    {seminar.description && (
                      <Text
                        fontSize="sm"
                        lineHeight="1.75"
                        color="#4c5955"
                        mb={5}
                      >
                        {seminar.description}
                      </Text>
                    )}

                    {seminar.speakers && (
                      <Stack spacing={2}>
                        {seminar.speakers.map((speaker) => (
                          <Box key={speaker.name}>
                            <Text fontSize="sm" fontWeight="600">
                              {speaker.name}
                            </Text>
                            <Text
                              fontSize="sm"
                              color="#6b7773"
                              lineHeight="1.6"
                            >
                              {speaker.role}
                            </Text>
                          </Box>
                        ))}
                      </Stack>
                    )}
                  </Box>

                  <Box gridColumn={{ md: "span 3" }}>
                    {seminar.output && (
                      <Box mb={5}>
                        <Text
                          fontSize="xs"
                          fontWeight="700"
                          letterSpacing="0.1em"
                          textTransform="uppercase"
                          color="#087f73"
                          mb={2}
                        >
                          Output
                        </Text>

                        <Text
                          fontSize="sm"
                          fontWeight="600"
                          color="#17211f"
                        >
                          {seminar.output}
                        </Text>
                      </Box>
                    )}

                    {seminar.followOn && (
                      <Box>
                        <Text
                          fontSize="xs"
                          fontWeight="700"
                          letterSpacing="0.1em"
                          textTransform="uppercase"
                          color="#087f73"
                          mb={2}
                        >
                          Follow-on
                        </Text>

                        <Text
                          fontSize="sm"
                          fontWeight="600"
                          color="#17211f"
                        >
                          {seminar.followOn}
                        </Text>
                      </Box>
                    )}

                    {seminar.summaryUrl && (
                      <Button
                        as="a"
                        href={seminar.summaryUrl}
                        bg="#087f73"
                        color="white"
                        borderRadius="full"
                        size="sm"
                        fontWeight="600"
                        mt={2}
                        _hover={{
                          bg: "#06695f",
                          textDecoration: "none",
                        }}
                      >
                        Read the Summary
                      </Button>
                    )}

                    {seminar.status === "Upcoming" && (
                      <Button
                        as="a"
                        href="#engage"
                        variant="outline"
                        borderColor="#17211f"
                        color="#17211f"
                        borderRadius="full"
                        size="sm"
                        fontWeight="600"
                        mt={2}
                        _hover={{ bg: "blackAlpha.50" }}
                      >
                        Express interest
                      </Button>
                    )}
                  </Box>
                </SimpleGrid>
              </Box>
            ))}
          </Stack>

          <Box
            mt={8}
            pt={7}
            borderTop="1px solid"
            borderColor="blackAlpha.200"
          >
            <Text
              fontSize="xs"
              fontWeight="700"
              letterSpacing="0.1em"
              textTransform="uppercase"
              color="#5c6965"
              mb={3}
            >
              Potential later topic
            </Text>
            <Heading
              as="h3"
              fontFamily="'Poppins', sans-serif"
              fontSize={{ base: "xl", md: "2xl" }}
              lineHeight="1.2"
              letterSpacing="-0.03em"
              fontWeight="600"
            >
              The Family as a Unit of Precision Health
            </Heading>
          </Box>
        </Container>
      </Box>

      {/* TRANSLATION */}
      <Box bg="#f9faf8">
        <Container
          maxW="1200px"
          px={{ base: 5, md: 8 }}
          py={{ base: 20, md: 28 }}
        >
          <Stack spacing={8}>
            <Text
              fontSize="xs"
              fontWeight="700"
              letterSpacing="0.16em"
              textTransform="uppercase"
              color="#087f73"
            >
              From research to translation
            </Text>

            <Heading
              fontFamily="'Poppins', sans-serif"
              fontSize={{ base: "3xl", md: "5xl" }}
              lineHeight="1.1"
              letterSpacing="-0.04em"
              fontWeight="600"
              maxW="900px"
            >
              Evidence is only the beginning.
            </Heading>

            <Text
              fontSize={{ base: "lg", md: "xl" }}
              lineHeight="1.8"
              color="#4c5955"
              maxW="850px"
            >
              The Institute works across the boundary between discovery and
              delivery. Research can identify a risk, biomarker, intervention
              or opportunity — but translation asks what has to happen next
              for that knowledge to become useful.
            </Text>

            <Flex flexWrap="wrap" gap={{ base: 3, md: 5 }} pt={4}>
              {[
                "Generate evidence",
                "Translate findings",
                "Build usable tools",
                "Test pathways",
                "Measure outcomes",
                "Learn and improve",
              ].map((item, index) => (
                <Flex key={item} align="center">
                  <Text
                    fontSize={{ base: "sm", md: "md" }}
                    fontWeight="600"
                    color="#17211f"
                  >
                    {item}
                  </Text>

                  {index < 5 && (
                    <Text
                      mx={{ base: 3, md: 5 }}
                      color="#087f73"
                      fontSize="lg"
                    >
                      →
                    </Text>
                  )}
                </Flex>
              ))}
            </Flex>
          </Stack>
        </Container>
      </Box>

      {/* OUTPUTS */}
      <Box bg="#17211f" color="white">
        <Container
          maxW="1200px"
          px={{ base: 5, md: 8 }}
          py={{ base: 20, md: 28 }}
        >
          <Stack spacing={6} mb={12}>
            <Text
              fontSize="xs"
              fontWeight="700"
              letterSpacing="0.16em"
              textTransform="uppercase"
              color="#7bcfc3"
            >
              Knowledge & outputs
            </Text>

            <Heading
              fontFamily="'Poppins', sans-serif"
              fontSize={{ base: "3xl", md: "5xl" }}
              lineHeight="1.1"
              letterSpacing="-0.04em"
              fontWeight="600"
              maxW="800px"
            >
              Knowledge should travel beyond the room.
            </Heading>

            <Text
              fontSize={{ base: "md", md: "lg" }}
              lineHeight="1.8"
              color="whiteAlpha.700"
              maxW="750px"
            >
              The Institute's work is designed to produce knowledge that can
              travel beyond the room — briefs, concept notes, seminar outputs
              and other resources that help move discussion toward action.
            </Text>
          </Stack>

          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5}>
            <Box
              border="1px solid"
              borderColor="whiteAlpha.200"
              p={{ base: 7, md: 9 }}
            >
              <Text
                fontSize="xs"
                fontWeight="700"
                letterSpacing="0.1em"
                textTransform="uppercase"
                color="#7bcfc3"
                mb={5}
              >
                Implementation Brief · August 2026
              </Text>

              <Heading
                fontFamily="'Poppins', sans-serif"
                fontSize="2xl"
                mb={4}
              >
                African Precision Health Implementation Brief — 2026
              </Heading>

              <Text
                fontSize="sm"
                lineHeight="1.75"
                color="whiteAlpha.700"
                mb={6}
              >
                From Innovation to Implementation: Building the Connective
                Capacity for Africa's Precision-Health Future.
              </Text>

              <Button
                as="a"
                href="/resources/african-precision-health-implementation-brief-2026.pdf"
                target="_blank"
                rel="noopener noreferrer"
                variant="outline"
                borderColor="whiteAlpha.400"
                color="white"
                borderRadius="full"
                size="sm"
                fontWeight="600"
                _hover={{
                  bg: "whiteAlpha.100",
                  textDecoration: "none",
                }}
              >
                View resource
              </Button>
            </Box>

            <Box
              border="1px solid"
              borderColor="whiteAlpha.200"
              p={{ base: 7, md: 9 }}
            >
              <Text
                fontSize="xs"
                fontWeight="700"
                letterSpacing="0.1em"
                textTransform="uppercase"
                color="#7bcfc3"
                mb={5}
              >
                Follow-on concept · August 2026
              </Text>

              <Heading
                fontFamily="'Poppins', sans-serif"
                fontSize="2xl"
                mb={4}
              >
                From Conversation to Implementation
              </Heading>

              <Text
                fontSize="sm"
                lineHeight="1.75"
                color="whiteAlpha.700"
                mb={6}
              >
                A 90-Day Mobilisation for African Precision Health, outlining
                a practical pathway from shared diagnosis toward defined
                implementation opportunities.
              </Text>

              <Button
                as="a"
                href="/resources/90-day-mobilisation-african-precision-health.pdf"
                target="_blank"
                rel="noopener noreferrer"
                variant="outline"
                borderColor="whiteAlpha.400"
                color="white"
                borderRadius="full"
                size="sm"
                fontWeight="600"
                _hover={{
                  bg: "whiteAlpha.100",
                  textDecoration: "none",
                }}
              >
                View resource
              </Button>
            </Box>
          </SimpleGrid>

          <Flex
            mt={8}
            justify="space-between"
            align={{ base: "flex-start", md: "center" }}
            direction={{ base: "column", md: "row" }}
            gap={5}
          >
            <Text
              fontSize="sm"
              color="whiteAlpha.600"
              maxW="650px"
              lineHeight="1.7"
            >
              More briefs, research outputs and seminar materials will be
              added as the Institute's work develops.
            </Text>

            <Button
              as="a"
              href="/resources"
              variant="outline"
              borderColor="whiteAlpha.400"
              color="white"
              borderRadius="full"
              px={6}
              fontWeight="600"
              _hover={{
                bg: "whiteAlpha.100",
                textDecoration: "none",
              }}
            >
              Explore all resources
            </Button>
          </Flex>
        </Container>
      </Box>

      {/* SCIENTIFIC EXCHANGE */}
      <Box bg="#f9faf8">
        <Container
          maxW="1400px"
          px={{ base: 5, md: 8, lg: 12 }}
          py={{ base: 20, md: 28 }}
        >
          <SimpleGrid
            columns={{ base: 1, lg: 2 }}
            spacing={{ base: 12, lg: 20 }}
            alignItems="center"
          >
            <Box
              borderRadius="2xl"
              overflow="hidden"
              order={{ base: 2, lg: 1 }}
            >
              <Image
                src="/images/institutional/serena3.png"
                alt="African researchers and health innovators participating in a scientific seminar"
                w="100%"
                h={{ base: "320px", md: "500px" }}
                objectFit="cover"
              />
            </Box>

            <Stack
              spacing={7}
              order={{ base: 1, lg: 2 }}
            >
              <Text
                fontSize="xs"
                fontWeight="700"
                letterSpacing="0.16em"
                textTransform="uppercase"
                color="#087f73"
              >
                Scientific exchange
              </Text>

              <Heading
                fontFamily="'Poppins', sans-serif"
                fontSize={{ base: "3xl", md: "5xl" }}
                lineHeight="1.1"
                letterSpacing="-0.04em"
                fontWeight="600"
              >
                Science gets stronger when people can exchange it.
              </Heading>

              <Text
                fontSize={{ base: "md", md: "lg" }}
                lineHeight="1.8"
                color="#4c5955"
              >
                The Institute creates spaces for researchers, clinicians,
                students and health innovators to interrogate evidence,
                exchange ideas and build collaborations across disciplines and
                institutions.
              </Text>
            </Stack>
          </SimpleGrid>
        </Container>
      </Box>

      {/* ENGAGEMENT */}
      <Box id="engage" bg="#f3f0e8">
        <Container
          maxW="1400px"
          px={{ base: 5, md: 8, lg: 12 }}
          py={{ base: 20, md: 28 }}
        >
          <Stack spacing={6} mb={{ base: 12, md: 16 }}>
            <Text
              fontSize="xs"
              fontWeight="700"
              letterSpacing="0.16em"
              textTransform="uppercase"
              color="#087f73"
            >
              Engage with the Institute
            </Text>

            <Heading
              fontFamily="'Poppins', sans-serif"
              fontSize={{ base: "3xl", md: "5xl" }}
              lineHeight="1.1"
              letterSpacing="-0.04em"
              fontWeight="600"
              maxW="850px"
            >
              Find your way into the work.
            </Heading>

            <Text
              fontSize={{ base: "md", md: "lg" }}
              lineHeight="1.8"
              color="#4c5955"
              maxW="800px"
            >
              Whether you are a researcher, clinician, institution, innovator
              or emerging scientist, there are multiple ways to participate in
              the work of the Institute.
            </Text>
          </Stack>

          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={5}>
            {engagementAreas.map((item) => (
              <Box
                key={item.title}
                bg="#f9faf8"
                border="1px solid"
                borderColor="blackAlpha.100"
                p={{ base: 6, md: 7 }}
              >
                <Heading
                  as="h3"
                  fontFamily="'Poppins', sans-serif"
                  fontSize="lg"
                  lineHeight="1.3"
                  mb={3}
                >
                  {item.title}
                </Heading>

                <Text
                  fontSize="sm"
                  lineHeight="1.7"
                  color="#5c6965"
                >
                  {item.text}
                </Text>
              </Box>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* COLLABORATION CTA */}
      <Box bg="#087f73" color="white">
        <Container
          maxW="1100px"
          px={{ base: 5, md: 8 }}
          py={{ base: 20, md: 24 }}
          textAlign="center"
        >
          <Stack spacing={7} align="center">
            <Text
              fontSize="xs"
              fontWeight="700"
              letterSpacing="0.16em"
              textTransform="uppercase"
              color="whiteAlpha.700"
            >
              Collaborate
            </Text>

            <Heading
              fontFamily="'Poppins', sans-serif"
              fontSize={{ base: "3xl", md: "5xl" }}
              lineHeight="1.1"
              letterSpacing="-0.04em"
              fontWeight="600"
              maxW="800px"
            >
              Have a question, idea or opportunity?
            </Heading>

            <Text
              fontSize={{ base: "md", md: "lg" }}
              lineHeight="1.7"
              color="whiteAlpha.800"
              maxW="700px"
            >
              Tell us what you are working on, what you are looking for, or
              where you think Serena Space could contribute.
            </Text>

            <ContactModal>
              Get in touch
            </ContactModal>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
}

export default InstitutePage;